import dayjs from "dayjs";
import { AlertCircle, CalendarCheck, PhoneCall, Sparkles, Trophy, Wallet } from "lucide-react";
import { Skeleton } from "../../../components/ui/skeleton";
import { formatINR } from "../../../components/dashboard/shared";
import { cn } from "../../../utils/tailwindUtils";

const TONES = {
    brand: "bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300",
    red: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300",
    green: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
    sky: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300",
};

const Tile = ({ label, value, sub, icon: Icon, tone = "brand", alert, onClick, loading }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            "text-left bg-white dark:bg-gray-800 rounded-2xl border p-4 shadow-sm transition",
            "hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
            alert ? "border-red-300 dark:border-red-700" : "border-gray-200/80 dark:border-gray-700",
        )}
    >
        <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <span className={cn("p-1.5 rounded-lg", TONES[tone])}><Icon className="h-4 w-4" /></span>
        </div>
        {loading ? <Skeleton className="h-7 w-16 mt-2" /> : (
            <p className={cn("text-2xl font-bold mt-1 tabular-nums",
                alert ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white")}>
                {value}
            </p>
        )}
        {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{sub}</p>}
    </button>
);

// The six numbers a rep checks first. Each opens the list behind it.
const TodayStrip = ({ today, loading, go }) => {
    const t = today || {};
    const oldest = t.uncontacted_oldest_at ? `oldest ${dayjs(t.uncontacted_oldest_at).fromNow(true)}` : "all caught up";
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            <Tile label="Due today" value={t.followups_due_today ?? 0} icon={PhoneCall}
                  sub="follow-ups" loading={loading} onClick={() => go("followup")} />
            <Tile label="Overdue" value={t.overdue ?? 0} icon={AlertCircle}
                  tone="red" alert={(t.overdue || 0) > 0}
                  sub={(t.overdue || 0) > 0 ? "clear these first" : "nothing overdue"}
                  loading={loading} onClick={() => go("followup")} />
            <Tile label="Demos today" value={t.demos_today ?? 0} icon={CalendarCheck} tone="sky"
                  sub="on the calendar" loading={loading} onClick={() => go("demos")} />
            <Tile label="New, not contacted" value={t.uncontacted_new ?? 0} icon={Sparkles} tone="amber"
                  sub={oldest} loading={loading} onClick={() => go("leads")} />
            {/* Weighted ₹ when deals are priced; otherwise the open count, since
                "₹0" would read as an empty pipeline. */}
            <Tile label="My pipeline" icon={Wallet}
                  value={t.pipeline_value ? formatINR(t.weighted_value) : (t.open_pipeline ?? 0)}
                  sub={t.pipeline_value
                      ? `${t.open_pipeline ?? 0} open · ${formatINR(t.pipeline_value)} total`
                      : "open leads · no prices quoted yet"}
                  loading={loading} onClick={() => go("leads")} />
            <Tile label="Won this month" value={t.won_this_month ?? 0} icon={Trophy} tone="green"
                  sub="closed deals" loading={loading} onClick={() => go("leads")} />
        </div>
    );
};

export default TodayStrip;
